import { getAppStore } from '~src/app/AppStore';
import { selectSpaceInfo } from '~src/app/selectors/SpaceInfoSelectors';
import { isServerTasksSpace, SpaceInfo } from '~src/app/state/SpaceInfo';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { GetFlowActionsRequest } from '~src/ts/ServerApi/requests/GetFlowActionsRequest';
import { GetRunFlowTaskSubscriptionFlowRequest,GetRunFlowTaskSubscriptionFlowResult } from '~src/ts/ServerApi/requests/GetRunFlowTaskSubscriptionFlowRequest';
import { FlowAllowedActions } from '~src/ts/ServerApi/types/FlowAllowedActions';

import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { WebHttpClientWithErrorToastInstance } from '../../httpClient/WebHttpClientWithErrorToast';
import { ServerApiMapper } from '../../ts/helpers/ServerModelHelper';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { IActionResource } from '../../ts/resources/Resource';
import { ResourceQuery } from '../../ts/resources/ResourceQuery';
import { DeleteRunFlowTasksRequest } from '../../ts/ServerApi/requests/DeleteRunFlowTasksRequest';
import { GetRunFlowTasksForServerRequest,GetRunFlowTasksRequest } from '../../ts/ServerApi/requests/GetRunFlowTasksRequest';
import { ResumeRunFlowTasksRequest } from '../../ts/ServerApi/requests/ResumeRunFlowTasksRequest';
import { RunFlowTasksRequest } from '../../ts/ServerApi/requests/RunFlowTasksRequest';
import { SetRunFlowTaskOutputStepsRequest } from '../../ts/ServerApi/requests/SetRunFlowTaskOutputStepsRequest';
import { SetRunFlowTasksPriorityRequest } from '../../ts/ServerApi/requests/SetRunFlowTasksPriorityRequest';
import { SetRunFlowTasksScheduleRequest } from '../../ts/ServerApi/requests/SetRunFlowTasksScheduleRequest';
import { IFlowRunSpecOutputStepViewModel } from '../../ts/ServerApi/types/IFlowRunSpecOutputStepViewModel';
import { FlowParameterParams } from '../types/FlowParameters';

export class RunFlowTasksResource implements IActionResource<FlowAllowedActions> {

  private siteFetcher = new WebClientListResourceFetcher(GetRunFlowTasksRequest, ServerApiMapper.runFlowTasks);
  private serverFetcher = new WebClientListResourceFetcher(GetRunFlowTasksForServerRequest, ServerApiMapper.runFlowTasks);

  public MAX_PRIORITY = 100;
  public MIN_PRIORITY = 1;

  public all(): ResourceQuery<RunFlowTaskInfo> {
    const appState = getAppStore().getState();
    const spaceInfo: SpaceInfo = selectSpaceInfo(appState);

    if (isServerTasksSpace(spaceInfo)) {
      return new ResourceQuery(this.serverFetcher);
    }

    return new ResourceQuery(this.siteFetcher);
  }

  public actions(flowId: string): Promise<FlowAllowedActions> {
    return WebHttpClient.sendRequest(new GetFlowActionsRequest(flowId));
  }

  public del(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteRunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public run(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public setPriority(ids: string[], priority: number): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetRunFlowTasksPriorityRequest(idsBlock, priority)),
      ResultHelper.combineResults);
  }

  public setSchedule(ids: string[], scheduleId: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetRunFlowTasksScheduleRequest(idsBlock, scheduleId)),
      ResultHelper.combineResults);
  }

  public getSubscriptionFlow(taskId: string): Promise<GetRunFlowTaskSubscriptionFlowResult> {
    return WebHttpClient.sendRequest(new GetRunFlowTaskSubscriptionFlowRequest(taskId))
      .then((result: GetRunFlowTaskSubscriptionFlowResult) => result);
  }

  public setOutputSteps(
    taskId: string,
    runAllOutputs: boolean,
    isRunAllIncremental: boolean,
    ids: string[],
    flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[],
    parameterParams?: FlowParameterParams[],
  ): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new SetRunFlowTaskOutputStepsRequest({
        id: taskId,
        runAllOutputs: runAllOutputs,
        isRunAllIncremental,
        flowOutputStepIds: ids,
        flowRunSpecOutputStepParams,
        parameterParams: parameterParams,
      })
    );
  }

  public resume(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new ResumeRunFlowTasksRequest(idsBlock)),
      ResultHelper.combineResults);
  }
}

export const RunFlowTasks = new RunFlowTasksResource();
